/** Audit-log file export (CSV / JSON) for the current filters. */
import axiosInstance, { handleSessionRejection } from 'services/axiosInstance'
import { isAuditServiceError } from 'services/auditLogs/errors'
import {
  buildAuditLogSearchParams,
  getAuditLogsBaseUrl,
  toQueryValues,
} from 'services/auditLogs/list'
import {
  AuditLogExportFormat,
  AuditLogQuery,
  AuditLogsApp,
  AuditServiceErrorBody,
} from 'services/auditLogs/types'

export const EXPORT_FAILED_ERROR: AuditServiceErrorBody = { error: { message: 'Export failed' } }

// Blob.text() is missing in older engines (and jsdom), so fall back to FileReader.
function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(blob)
  })
}

// With responseType 'blob' axios hands the JSON error envelope back as a Blob too.
async function parseBlobError(data: unknown): Promise<AuditServiceErrorBody> {
  if (!(data instanceof Blob)) {
    return isAuditServiceError(data) ? data : EXPORT_FAILED_ERROR
  }
  try {
    const parsed: unknown = JSON.parse(await readBlobText(data))
    return isAuditServiceError(parsed) ? parsed : EXPORT_FAILED_ERROR
  } catch {
    return EXPORT_FAILED_ERROR
  }
}

export async function exportAuditLogs(
  app: AuditLogsApp,
  format: AuditLogExportFormat,
  query: AuditLogQuery,
): Promise<Blob | AuditServiceErrorBody> {
  const search = buildAuditLogSearchParams({ format, ...toQueryValues(query) })
  return axiosInstance
    .get(`${getAuditLogsBaseUrl(app)}/export?${search.toString()}`, { responseType: 'blob' })
    .then(res => res.data as Blob)
    .catch(async error => {
      if (!error?.response) return Promise.reject(error)
      const parsed = await parseBlobError(error.response.data)
      // The interceptor could not read the auth code out of the Blob, so the logout runs here.
      handleSessionRejection(error, parsed.error.code)
      return parsed
    })
}
